// Third-party imports
import { useAtom }                                        from "jotai";
import { FieldErrors, FieldValues, useForm, UseFormGetValues, UseFormReset }                                        from "react-hook-form";
import { ReactNode, useEffect, useMemo, useState, useCallback }                 from "react";
import { useLoaderData, useNavigate, useLocation }                         from "react-router-dom";
import { InfoWindow, Map, AdvancedMarker, useMap }                        from '@vis.gl/react-google-maps';
import { CloseButton, Col, Container, Row, Tab, Tabs }    from "react-bootstrap";

// App imports
import { Amenity }                  from "./Data/Amenity";
import { Button }                   from "./Controls/Button";
import { AmenitiesModal }           from "./Controls/AmenitiesModal";
import { AmenitiesButton }          from "./Controls/AmenitiesButton";
import { HourlySearch }             from "./Controls/HourlySearch";
import { MonthlySearch }            from "./Controls/MonthlySearch";
import { RatesModal }               from "./Controls/RatesModal";
import { ResultHourlyEntry }        from "./Controls/ResultHourlyEntry";
import { ResultMonthlyEntry }       from "./Controls/ResultMonthlyEntry";
import { FacilityHourlyDetails }    from "./Controls/FacilityHourlyDetails";
import { FacilityPlanDetails }   from "./Controls/FacilityPlanDetails";
import { createLocalDate, createLocalDateTime, showBrowserDate, showBrowserTime } from "./Data/Date";
import { HourlyForm, MonthlyForm, SearchActivity, createHourlyForm, createMonthlyForm,
         hourlyFormAtom, monthlyFormAtom, tabAtom } from "./Data/Atoms";
import { cents, max, min, showMoneyShort } from "./Data/Money";
import { computeCharge, getNonEventRates } from "./Data/Rates";
import { LocalDateTime } from "@js-joda/core";
import { Facility, RateProgram } from "./Data/ApiTypes";
import { useOptionalParamIds } from "./Data/Common";
import React from "react";

interface MapHandlerProps {
    place: {
        viewport: {
            north: number;
            south: number;
            east: number;
            west: number;
        };
        name: string;
        formatted_address: string;
    } | null;
}

const MapHandler = ( { place }: MapHandlerProps ) => {
    const map = useMap();
    useEffect( () => {
        if( !map || !place ) {
            return;
        }
        const bounds = new google.maps.LatLngBounds(
            { lat: place.viewport.south, lng: place.viewport.west },
            { lat: place.viewport.north, lng: place.viewport.east }
        );
        map.fitBounds(bounds);
    }, [map, place] );
    return null;
};

type PlaceState = MapHandlerProps['place'];

interface FacilityDetailsColProps {
    children:            ReactNode;
    setSelectedFacility: () => void;
}

const FacilityDetailsCol = ( props: FacilityDetailsColProps ) => {
    const { setSelectedFacility, children } = { ...props };
    return <Col md={7} lg={6} xl={5} xxl={4}
                className="bg-white fullheight p-3 scroll-y"
                style={{ position: "relative" }}>
        <div>
            <CloseButton className="shadow p-3 bg-white rounded-5 m-1 fs-4"
                style={{ position: "absolute", top: "3%", right: "5%", opacity: 1.0 }}
                onClick={() => setSelectedFacility()} />
        </div>
        <div>
            {children}
        </div>
    </Col>;
}

export interface ResultPageMode<TSearchForm extends FieldValues> {
    createForm:   () => TSearchForm; //for resetting the form
    searchFilter: ( searchForm: TSearchForm, facility: Facility ) => boolean;
    setForm:      ( searchForm: TSearchForm ) => void;
    resetForm:    UseFormReset<TSearchForm>;
}

function getPriceForMapPin( tab: SearchActivity, f: Facility, startDateTime: LocalDateTime, endDateTime: LocalDateTime ) {
    let price = cents( 0 );
    const nonEventRates = getNonEventRates(f.hourlyRates);
    if( tab === SearchActivity.Hourly ) {
        price = computeCharge( nonEventRates, startDateTime, endDateTime );
        if ( price.toJSON().amount === Number.MAX_SAFE_INTEGER ) {
            price = cents( 0 );
        }
    }
    if( tab === SearchActivity.Monthly ) {
        const prices = f.listedPlans.map( listedPlan => listedPlan.price );
        const [minPrice, maxPrice] = prices.length > 0 ? [min( prices ), max( prices )] : [cents(0), cents(0)];
        price = minPrice;
    }
    return price;
}

interface SearchActivityFuncs<TForm extends FieldValues> {
    createForm:   () => TForm;
    searchForm:   TForm;
    searchFilter: ( searchForm: TForm, facility: Facility ) => boolean;
    setForm:      ( searchForm: TForm ) => void;
    resetForm:    UseFormReset<TForm>;
    getValues:    UseFormGetValues<TForm>;
    getEntry:     ( facility:         Facility ) => JSX.Element;
    getDetails:   ( selectedFacility: Facility ) => JSX.Element;
}

export function ResultsPage() {
    //router stuff
    const results          = useLoaderData() as Facility[]; //from the loader
    const nav              = useNavigate();
    const { facilityId }   = useOptionalParamIds();
    const location = useLocation();

    const [place,        setPlace       ] = useState<PlaceState>(location.state?.place || null);
    const [showRates,    setShowRates   ] = useState<RateProgram[]>();
    const [showFilters,  setShowFilters ] = useState( false );
    const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [facilityDistances, setFacilityDistances] = useState<Record<number, number>>({});

    function handlePlaceSelect(p: google.maps.places.Place | null) {
        if (p?.viewport) {
            setPlace({
                viewport: {
                    north: p.viewport.getNorthEast().lat(),
                    south: p.viewport.getSouthWest().lat(),
                    east: p.viewport.getNorthEast().lng(),
                    west: p.viewport.getSouthWest().lng()
                },
                name: p.displayName || '',
                formatted_address: p.formattedAddress || ''
            });
            submit();
        } else {
            setPlace(null);
        }
    }

    //set to true to search on page load
    const [requiredAmenities, setReqAmenities] = useState<Amenity[]>(  [] );

    const [tab,     setTab]     = useAtom( tabAtom         );
    const [hourly,  setHourly]  = useAtom( hourlyFormAtom  );
    const [monthly, setMonthly] = useAtom( monthlyFormAtom );

    const { register: regHr, handleSubmit: handleSubmitHr, control: controlHr, watch: watchHr, getValues: getValuesHr, reset: resetHr, formState: formStateHr, setValue: setValueHr } = useForm<HourlyForm>(  { defaultValues: hourly  } );
    const { register: regMo, handleSubmit: handleSubmitMo, control: controlMo, watch: watchMo, getValues: getValuesMo, reset: resetMo, formState: formStateMo, setValue: setValueMo } = useForm<MonthlyForm>( { defaultValues: monthly } );

    const startDateTime = useMemo(() => createLocalDateTime( hourly.enterAfterDate, hourly.enterAfterTime ), [hourly.enterAfterDate, hourly.enterAfterTime]);
    const endDateTime   = useMemo(() => createLocalDateTime( hourly.exitBeforeDate, hourly.exitBeforeTime ), [hourly.exitBeforeDate, hourly.exitBeforeTime]);
    const startDate     = useMemo(() => createLocalDate( monthly.startDate ), [monthly.startDate]);

    // Time change handlers for hourly details - using functional setState to avoid stale closures
    const handleStartChange = (newStart: LocalDateTime) => {
        setHourly(prev => ({
            ...prev,
            enterAfterDate: showBrowserDate(newStart.toLocalDate()),
            enterAfterTime: showBrowserTime(newStart.toLocalTime())
        }));
    };

    const handleEndChange = (newEnd: LocalDateTime) => {
        setHourly(prev => ({
            ...prev,
            exitBeforeDate: showBrowserDate(newEnd.toLocalDate()),
            exitBeforeTime: showBrowserTime(newEnd.toLocalTime())
        }));
    };     

    function hourlySearchFilter( form: HourlyForm, facility: Facility ) {
        return facility.hourlyRates.length > 0;
    }

    function monthlySearchFilter( form: MonthlyForm, facility: Facility ) {
        return facility.listedPlans.length > 0;
    }

    
    const {
        createForm,
        searchForm,
        searchFilter,
        setForm,
        resetForm,
        getValues,
        getEntry,
        getDetails
    } = getResultsImpl( tab );

    useEffect(() => {
    if (place) {
        setValueHr("location", place.formatted_address); 
        setValueMo("location", place.formatted_address); 
    }
    }, [place]);

    const filteredFacilities = useMemo(() => {
        if (!selectedLocation) return [];
              const resultFilter = results
          .filter(f => searchFilter(searchForm, f))
          .filter(f => {
            if (!f.coordinates) return false;
            //if searching hourly and facility only has non-events, ignore it
            if (tab === SearchActivity.Hourly && getNonEventRates(f.hourlyRates).length === 0) {
              return false;
            }
            const { lat: facilityLat, lng: facilityLng } = f.coordinates;
            const distance = calculateDistance(selectedLocation.lat, selectedLocation.lng, facilityLat, facilityLng);
            return distance <= 50; 
          });
      
        return resultFilter;
      }, [results, selectedLocation, searchFilter, searchForm]);

    useEffect(() => {
        const newDistances: Record<number, number> = {};
      
        filteredFacilities.forEach(f => {
          const { lat: facilityLat, lng: facilityLng } = f.coordinates;
          const distance = calculateDistance(selectedLocation!.lat, selectedLocation!.lng, facilityLat, facilityLng);
      
          if (distance <= 50) {
            newDistances[f.facilityId] = distance;
          }
        });
      
        setFacilityDistances(prev => {
          const hasChanges = Object.keys(newDistances).some(
            key => newDistances[+key] !== prev[+key]
          );
          return hasChanges ? newDistances : prev;
        });
      }, [filteredFacilities, selectedLocation]);

    const [walkingTimes, setWalkingTimes] = useState<{ [key: number]: string  }>({});
    const map = useMap();

    // Find furthest facility to center map
    const furthestFacility = useMemo(() => {
        if (!selectedLocation || filteredFacilities.length === 0) return null;

        return filteredFacilities.reduce((furthest, current) => {
            if (!furthest || !current.coordinates) return current;

            const furthestDistance = calculateDistance(
                selectedLocation.lat, 
                selectedLocation.lng, 
                furthest.coordinates.lat, 
                furthest.coordinates.lng
            );

            const currentDistance = calculateDistance(
                selectedLocation.lat, 
                selectedLocation.lng, 
                current.coordinates.lat, 
                current.coordinates.lng
            );

            return currentDistance > furthestDistance ? current : furthest;
        });
    }, [selectedLocation, filteredFacilities]);

    // Center map on furthest facility
    useEffect(() => {
        if (furthestFacility?.coordinates && selectedLocation) {
            const bounds = new google.maps.LatLngBounds();
            bounds.extend(selectedLocation);
            bounds.extend(furthestFacility.coordinates);
            map!.fitBounds(bounds, {
                top: 100,
                bottom: 100,
                left: 100,
                right: 100
            });
        }
    }, [furthestFacility, selectedLocation, map]);

    function formatWalkingTime(distance: number): string {
        const maxWalkingMiles = 1.875; // ~30 minutes walk max
        if (distance < maxWalkingMiles) {
          let minutes = (distance * 15); 
      
          if (minutes < 1) {
            return "~ less than 1 min walk";
          } else {
            return `~${Math.round(minutes)} min walk`;
          }
        } else {
          let miles = parseFloat(distance.toFixed(2));
          let formatted = miles.toString().replace(/^0\./, '.'); 
          return `${formatted} mi.`;
        }
      }

      const calculateWalkingTimes = useCallback((facilities: Facility[]) => {
        if (!selectedLocation || facilities.length === 0) return;

        const times: { [key: number]: string } = {};

        for (const f of facilities) {
          const distance = facilityDistances[f.facilityId];
          if (distance !== undefined) {
            times[f.facilityId] = formatWalkingTime(distance); 
          }
        }
      
        setWalkingTimes(prev => {
          const changed = Object.keys(times).some(k => times[+k] !== prev[+k]);
          return changed ? times : prev;
        });

    }, [selectedLocation, facilityDistances]);

    useEffect(() => {
        if (selectedLocation && filteredFacilities.length > 0) {
            calculateWalkingTimes(filteredFacilities);
        }
    }, [selectedLocation, calculateWalkingTimes]);

    function getHourlyEntry( f: Facility ) {
        return <ResultHourlyEntry
            key={f.facilityId}
            facility={f}
            onClick={ () => selectFacility( f.facilityId ) }
            onRatesClick={ () => setShowRates( getNonEventRates(f.hourlyRates) ) }
            start={startDateTime}
            end={endDateTime}
            className={ selectedFacility === f ? "border-primary" : "" }
            walkingTime={walkingTimes[f.facilityId]}
        />;
    }

    function getMonthlyEntry( f: Facility ) {
        return <ResultMonthlyEntry
            key={f.facilityId}
            facility={f}
            start={startDate}
            onClick={ () => selectFacility( f.facilityId ) }
            className={ selectedFacility === f ? "border-primary" : "" }
            walkingTime={walkingTimes[f.facilityId]}
        />
    }

    function getHourlyDetails( selectedFacility: Facility ) {
        return <FacilityDetailsCol setSelectedFacility={ () => nav( -1 ) }>
            <FacilityHourlyDetails
                facility={selectedFacility}
                start={startDateTime}
                end={endDateTime}
                onRatesClick={ () => setShowRates( getNonEventRates(selectedFacility.hourlyRates) ) }
                onStartChange={handleStartChange}
                onEndChange={handleEndChange} />
        </FacilityDetailsCol>;
    }

    function getMonthlyDetails( selectedFacility: Facility ) {
        return <FacilityDetailsCol setSelectedFacility={ () => nav( -1 ) }>
            <FacilityPlanDetails facility={selectedFacility} start={startDate} />
        </FacilityDetailsCol>;
    }

    function getResultsImpl<T extends HourlyForm | MonthlyForm>( tab: SearchActivity ): SearchActivityFuncs<T> {
        if( tab === SearchActivity.Hourly ) {
            const hourlyArr: SearchActivityFuncs<HourlyForm> = {
                createForm:   createHourlyForm,
                searchForm:   hourly,
                searchFilter: hourlySearchFilter,
                resetForm:    resetHr,
                setForm:      setHourly,
                getValues:    getValuesHr,
                getEntry:     getHourlyEntry,
                getDetails:   getHourlyDetails
            }
            return hourlyArr as any;
        }
        const monthlyArr: SearchActivityFuncs<MonthlyForm> = {
            createForm:   createMonthlyForm,
            searchForm:   monthly,
            searchFilter: monthlySearchFilter,
            resetForm:    resetMo,
            setForm:      setMonthly,
            getValues:    getValuesMo,
            getEntry:     getMonthlyEntry,
            getDetails:   getMonthlyDetails
        }
        return monthlyArr as any;
    }

    function selectFacility( id: number | undefined ) {
        if( id === undefined ) {
            nav( "/results" );
            return;
        }
        //the bang bang !! used for the replace is a clever trick for implementing
        //that if someone presses on a facility to view details
        //and then clicks on another facility, they won't both be
        //pushed to the top history stack, we will replace the
        //current page
        nav( `/results/${id}`, { replace: !!facilityId } );
    }

    function reset() {
        setForm(   { ...createForm(), location: getValues("location") } );
        resetForm( { ...createForm(), location: getValues("location") } );
    }

    function submit() {
        setForm( getValues() );
        return;
    }   

    // function amenityFilter( f: Facility ) {
    //     for( let a of requiredAmenities ) {
    //         if( f.amenities.findIndex( el => el === a ) === -1 ) {
    //             return false;
    //         }
    //     }
    //     return true;
    // }

    async function getAddressCoordinates(address: string): Promise<{ lat: number; lng: number }> {
        const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=AIzaSyDfdr9BkDEl6lj-Sxoka0CgmAA9R76gH2k`);
        const data = await response.json();
      
        if (data.status === "OK") {
          const location = data.results[0].geometry.location;
          return { lat: location.lat, lng: location.lng };
        } else {
          throw new Error("Geocoding failed: " + data.status);
        }
    }

    function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const toRad = (value: number) => (value * Math.PI) / 180;
        const R = 3958.8; // Radius of Earth in miles
      
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
      
        const a = Math.sin(dLat / 2) ** 2 +
                  Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
                  Math.sin(dLon / 2) ** 2;
      
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      
        return R * c;
    }

    const selectedFacility = facilityId ? results.find( f => f.facilityId === facilityId ) : undefined;
    let facilityDetails = <></>;
    if( selectedFacility ) {
        facilityDetails = getDetails( selectedFacility );
    }

    useEffect(() => {
        if (searchForm.location) {
           getAddressCoordinates(searchForm.location)
                .then(coords => {
                    setSelectedLocation(coords);
                })
                .catch(err => console.error(err));        
        }
    }, [place, searchForm.location]);

    const hideListClassNames  = selectedFacility ? "d-none" : "";

      
    const searchResultEntries = filteredFacilities.map( (f, index) => {
        const entry = getEntry(f);
        return <div key={f.facilityId} className={`slide-in ${index < 5 ? `slide-in-delay-${index + 1}` : ''}`}>
            {entry}
        </div>;
    });

    return <div className="fullheight">
      
        <AmenitiesModal show={showFilters}
                        setShow={setShowFilters}
                        amenities={requiredAmenities}
                        setAmenities={setReqAmenities} />

        <RatesModal show={showRates}
                    setShow={setShowRates} />

        <Container fluid className="fullheight">
            <Row className="fullheight justify-content-start">
                <Col className={`${hideListClassNames} d-xl-block bg-white scroll-y fullheight pt-2`} md={7} lg={6} xl={5} xxl={4}>
                    <Tabs defaultActiveKey="hourly"
                          activeKey={tab}
                          onSelect={ k => setTab( k as SearchActivity )}
                          className="mb-3 w-100"
                          variant="underline"
                          justify>
                        <Tab eventKey="hourly"
                             title="Hourly">
                            <HourlySearch
                                register={regHr}
                                watch={watchHr}
                                control={controlHr}
                                setValue={setValueHr}
                                errors={formStateHr.errors}
                                setPlace={handlePlaceSelect} />
                        </Tab>
                        <Tab eventKey="monthly"
                             title="Monthly">
                            <MonthlySearch
                                register={regMo}
                                watch={watchMo}
                                control={controlMo}
                                setValue={setValueMo}
                                errors={formStateMo.errors}
                                setPlace={handlePlaceSelect} />
                        </Tab>
                    </Tabs>
                    <div className="d-flex justify-content-center h-auto mb-3">
                        <Button className="me-2 w-100"
                            onClick={ reset } >
                            Today
                        </Button>
                        <AmenitiesButton className="me-2 w-100 text-black" style={{ visibility: "hidden" }}
                            onClick={ () => setShowFilters( true ) }
                            amenities={requiredAmenities} >
                                Amenities
                        </AmenitiesButton>
                        <Button
                        className="w-100 btn-main"
                        onClick={() => {
                            if (tab === "hourly") {
                            handleSubmitHr(submit)();
                            } else {
                            handleSubmitMo(submit)();
                            }
                        }}
                        >
                        Search
                        </Button>
                    </div>
                    {searchResultEntries}
                    {selectedLocation && (!searchResultEntries || searchResultEntries.length === 0) && 
                        <div className="alert alert-purple mt-4 d-flex align-items-center gap-2" style={{ backgroundColor: 'var(--color-light)' }}>                   
                            Sorry, no parking is available that meets your criteria.
                        </div>
                    }
                </Col>
                {selectedFacility && facilityDetails}
                <Col className="d-none d-md-block p-0">
                <Map 
                    defaultCenter={{ lat: 39.299236, lng: -76.609383 }}
                    defaultZoom={13}
                    mapId="6d064567c51cdbc9"
                    >
                    {selectedLocation && (
                    <AdvancedMarker position={selectedLocation}>
                    <img
                        src="/img/address-pin.png"
                        style={{ width: 68, height: 90 }}
                        alt="Address Pin"
                    />
                    </AdvancedMarker>
                    )}
                    {filteredFacilities.map(f => {
                        function clickFacility(ev: any): void {
                        selectFacility(f.facilityId);
                        }
                        if (f === selectedFacility) {
                            return <React.Fragment key={f.facilityId} />;
                        }
                        const priceForMapPin = getPriceForMapPin(tab, f, startDateTime, endDateTime);
                        const amount = priceForMapPin.toJSON().amount || 0;

                        if (amount > 0 && amount !== Number.MAX_SAFE_INTEGER) {
                        return (
                        <AdvancedMarker
                        key={f.facilityId}
                        position={f.coordinates}
                        onClick={clickFacility}
                        zIndex={f.facilityId}
                        >
                        <div style={{ position: 'relative', width: 110, height: 142 }}>
                            <img src="/img/lot-pin-bubble.png" style={{ width: '100%' }} />
                            <span style={{
                            position: 'absolute',
                            top: '19px',
                            left: '0',
                            width: '100%',
                            textAlign: 'center',
                            fontWeight: '900',
                            fontSize: '1.4em',
                            color: 'black'
                            }}>
                            {showMoneyShort(priceForMapPin)}
                            </span>
                        </div>
                        </AdvancedMarker>
                        );
                        }
                        return null;
                    })}
                    {selectedFacility &&
                        <InfoWindow onClose={ () => selectFacility( undefined ) }
                                    zIndex={1000}
                                    headerDisabled
                                    position={selectedFacility.coordinates}>
                            <div className="p-1" style={{ display: "grid", gridTemplateColumns: "auto auto", gap: "0.5em" }}>
                                <div className="fw-bold fs-6 grid-col-1">
                                    {selectedFacility.name}
                                </div>
                                <div className="fw-bold fs-6 grid-col-2 text-end">
                                    {showMoneyShort( getPriceForMapPin( tab, selectedFacility, startDateTime, endDateTime ) )}
                                </div>
                                <div className="grid-col-1 d-flex flex-row align-items-center">
                                    {/* <PersonWalking /> {selectedFacility.walkTime} min */}
                                    {/* <PersonWalking /> 5 min */}
                                </div>
                                <div />
                            </div>
                        </InfoWindow>}
                        <MapHandler place={place} />
                    </Map>
                </Col>
            </Row>
        </Container>
    </div>;
}

